import { Button, Dialog, Input } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactPresente } from '../../api/contacts.js';
import { estApiError } from '../../api/errors.js';
import { useMarquerVendue, type OeuvrePresentee } from '../../api/oeuvres.js';
import { Field } from '../Field.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import styles from './OeuvreDetailPage.module.css';

function aujourdhui(): string {
  return new Date().toISOString().slice(0, 10);
}

/** « 12 000 », « 12000,50 » → centimes ; `null` si la saisie n'est pas un montant. */
function versCentimes(saisie: string): number | null {
  const nettoyee = saisie.replace(/\s/g, '').replace(',', '.');
  if (nettoyee === '') return null;
  const euros = Number(nettoyee);
  return Number.isFinite(euros) && euros >= 0 ? Math.round(euros * 100) : null;
}

export interface MarquerVendueDialogProps {
  galerieSlug: string;
  oeuvre: OeuvrePresentee;
  ouvert: boolean;
  onOuvertChange: (ouvert: boolean) => void;
  /**
   * La facturation est active : la vente simple est alors l'action SECONDAIRE,
   * et le dialogue le dit — aucune facture ne sera émise.
   */
  facturationActive?: boolean;
}

/**
 * « Marquer vendue » (D21, cœur) : la vente SIMPLE, sans facture — un
 * acheteur du carnet, un prix, une date. La facture est le module
 * `facturation` ; ce geste est celui d'une galerie qui ne l'a pas. L'API
 * refuse ce que la table des transitions n'admet pas, et une œuvre réservée
 * pour un autre contact (`POST …/oeuvres/:id/vente`).
 */
export function MarquerVendueDialog({
  galerieSlug,
  oeuvre,
  ouvert,
  onOuvertChange,
  facturationActive = false,
}: MarquerVendueDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const marquer = useMarquerVendue(galerieSlug);
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [prix, setPrix] = useState(
    oeuvre.prixPublicCentimes === null ? '' : String(oeuvre.prixPublicCentimes / 100),
  );
  const [venduLe, setVenduLe] = useState(aujourdhui);
  const [erreur, setErreur] = useState<string | null>(null);

  const prixCentimes = versCentimes(prix);

  async function confirmer(): Promise<void> {
    if (!contact || prixCentimes === null) return;
    setErreur(null);
    try {
      await marquer.mutateAsync({
        id: oeuvre.id,
        corps: { contactId: contact.id, prixCentimes, devise: oeuvre.devise, venduLe },
      });
      onOuvertChange(false);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <Dialog
      open={ouvert}
      onOpenChange={onOuvertChange}
      title={
        facturationActive
          ? t('regie:oeuvres.venteSimple.titreSansFacture', { titre: oeuvre.titre })
          : t('regie:oeuvres.venteSimple.titre', { titre: oeuvre.titre })
      }
      description={t('regie:oeuvres.venteSimple.description')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOuvertChange(false)}>
            {t('commun:actions.annuler')}
          </Button>
          <Button
            variant="accent"
            onClick={() => void confirmer()}
            disabled={!contact || prixCentimes === null || venduLe === ''}
            loading={marquer.isPending}
          >
            {facturationActive
              ? t('regie:oeuvres.venteSimple.confirmerSansFacture')
              : t('regie:oeuvres.venteSimple.confirmer')}
          </Button>
        </>
      }
    >
      <div className={styles.formulaireCorrection}>
        <div role="group" aria-labelledby="vente-simple-acheteur">
          <p id="vente-simple-acheteur" className={styles.libelleGroupe}>
            {t('regie:oeuvres.venteSimple.acheteur')}
          </p>
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={contact}
            onChoisir={setContact}
            onEffacer={() => setContact(null)}
          />
        </div>
        <Field
          label={t('regie:oeuvres.venteSimple.prix', { devise: oeuvre.devise })}
          htmlFor="vente-simple-prix"
        >
          <Input
            id="vente-simple-prix"
            mono
            inputMode="decimal"
            value={prix}
            onChange={(event) => setPrix(event.target.value)}
          />
        </Field>
        <Field label={t('regie:oeuvres.venteSimple.date')} htmlFor="vente-simple-date">
          <Input
            id="vente-simple-date"
            type="date"
            value={venduLe}
            onChange={(event) => setVenduLe(event.target.value)}
          />
        </Field>
        {facturationActive ? (
          <p className={styles.avertissement} role="note">
            {t('regie:oeuvres.venteSimple.avertissementFacturation')}
          </p>
        ) : (
          <p className={styles.aide}>{t('regie:oeuvres.venteSimple.sansFacture')}</p>
        )}
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
